// CloudFront/ALB reject very long request URLs with a 414, which presents as
// the form silently never loading. 1800 leaves headroom under common 2k limits.
export const MAX_SRC_LENGTH = 1800;

// Exactly the params the form page consumes, in the order they matter. utm_id
// is the join key back to the CRM's attribution record, so it goes first and is
// the last thing the length guard can reach.
//
// An allowlist, not a denylist of sensitive names. A practice's landing page can
// carry patient data in its own query string, and any key the form page does not
// consume would cross onto an aivry domain and into our access logs only to be
// discarded on arrival — exposure with nothing bought for it. Keep in step with
// CONTEXT_KEYS in apps/marketing/app/forms/[embedKey]/lib/context.ts: a key
// added here that is missing there forwards data nobody reads, and a key added
// there that is missing here never arrives.
const FORWARDED_KEYS = [
  "utm_id",
  "gclid",
  "fbclid",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
];

// Identifies a page without carrying its query string or fragment across the
// iframe boundary. A dental practice's landing page can put patient details in
// its own URL, and forwarding those wholesale would move personal data onto an
// aivry domain and into our access logs, which is a HIPAA exposure we have no
// reason to take on: origin + path is all that is needed to know which page a
// lead came from, and the attribution params we actually consume are forwarded
// individually and allowlisted on the far side. Returns "" for anything that
// is not a parseable absolute URL, including the empty referrer.
function pageIdentity(rawUrl: string): string {
  try {
    const parsed = new URL(rawUrl);
    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    return "";
  }
}

export function buildIframeSrc(
  baseUrl: string,
  parentSearch: string,
  parentHref: string,
  parentReferrer: string,
): string {
  const url = new URL(baseUrl);

  // Keys are matched and forwarded lower-cased: some email builders and ad
  // tools emit UTM_ID, and the form page's allowlist is lower-case only, so
  // forwarding the parent's casing verbatim would drop the join key.
  const forwarded = new Map<string, string[]>();
  for (const [key, value] of new URLSearchParams(parentSearch)) {
    const canonical = key.toLowerCase();
    if (!FORWARDED_KEYS.includes(canonical)) continue;
    const values = forwarded.get(canonical);
    if (values) values.push(value);
    else forwarded.set(canonical, [value]);
  }

  // Attribution first, then the script-owned page identity, so that when the
  // length guard has to drop something it is never the join key. Parent params
  // cannot spoof the aivry_ keys: those names are not on the allowlist, so
  // nothing from the parent query is ever appended under them.
  const candidates: [string, string][] = [];
  for (const key of FORWARDED_KEYS) {
    for (const value of forwarded.get(key) ?? []) candidates.push([key, value]);
  }
  const pageUrl = pageIdentity(parentHref);
  const referrer = pageIdentity(parentReferrer);
  if (pageUrl) candidates.push(["aivry_page_url", pageUrl]);
  if (referrer) candidates.push(["aivry_referrer", referrer]);

  // Snapshot the base URL's own keys rather than testing has() inside the loop,
  // which would drop the second value of a repeated parent key (?utm_id=a&utm_id=b)
  // once the first append makes has("utm_id") true.
  const baseKeys = new Set(url.searchParams.keys());

  // The cap is measured against URLSearchParams' own serialization (via a
  // candidate clone) rather than estimated with encodeURIComponent — the two
  // disagree on characters like !'() and an estimate can undercount, letting
  // the final URL creep past the cap. The aivry_ keys are measured with
  // everything else, so a long landing-page path cannot push the src past the
  // cap this guard exists to enforce.
  const dropped: string[] = [];
  for (const [key, value] of candidates) {
    if (baseKeys.has(key)) continue;
    const candidate = new URL(url);
    candidate.searchParams.append(key, value);
    if (candidate.toString().length > MAX_SRC_LENGTH) {
      dropped.push(key);
      continue;
    }
    url.searchParams.append(key, value);
  }
  if (dropped.length > 0) {
    console.warn(
      `aivry.js: form URL would exceed ${MAX_SRC_LENGTH} chars; dropped params: ${dropped.join(", ")}`,
    );
  }

  return url.toString();
}
